
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const MVV_BP_DESKTOP = 768;
const MVV_SCROLL_TRIGGER_START = "top top";
const MVV_SCROLL_TRIGGER_END = "bottom bottom";
const MVV_NEXT_CARD_PROGRESS_FACTOR = 1.5;
const MVV_ACTIVE_POINTER_THRESHOLD = 0.5;
const MVV_NEXT_POINTER_THRESHOLD = 0.5;

/**
 * アニメーション用パラメータをまとめて管理する定数
 *
 * 例: MVV_CARD_PARAMS.mobile.minScale で参照
 */
const MVV_CARD_PARAMS = {
  mobile: {
    minScale: 0.93,
    liftY: 28,
    nextStartY: 72,
    fadeOutFactor: 0.5,
  },
  desktop: {
    minScale: 0.88,
    liftY: 40,
    nextStartY: 100,
    fadeOutFactor: 0.6,
  },
};

/**
 * MVVカードのスクロールアニメーションを初期化する関数
 *
 * - スクロール量に応じてカードの重なり・拡大縮小・透明度・クリック可否を制御する
 * - 1枚ずつカードが重なりながら切り替わる演出
 *
 */
export function initMvvCards() {
  "use strict";


  // --- 1. 必要な要素を取得 ---
  const outer = document.querySelector(".mvv__stackOuter"); // カード全体を囲む要素
  const cards = Array.from(document.querySelectorAll(".js-mvvCard[data-mvv-idx]")); // 各カード
  let trigger = null;

  // 要素がなければ何もしない
  if (!outer || cards.length === 0) {
    return;
  }

  // --- 2. カードのスタイルをリセットする関数 ---
  /**
   * 全カードのインラインスタイルを初期化する
   * （リサイズ時や範囲外のときに呼ばれる）
   */
  const resetCardStyles = function() {
    cards.forEach(function(card) {
      card.style.transform = "";
      card.style.opacity = "";
      card.style.zIndex = "";
      card.style.pointerEvents = "";
    });
  };

  // --- 3. スクロール量に応じてカードの見た目を更新する関数 ---
  /**
   * スクロール位置に応じて各カードのtransform, opacity, zIndex, pointerEventsを設定
   *
   * - activeIndex: 現在表示中のカード
   * - sectionProgress: 現在カード内での進捗（0〜1）
   * - モバイル/PCでパラメータを切り替え
   */
  const update = function(progress = 0) {
    const clampedProgress = Math.max(0, Math.min(1, progress));
    const range = outer.offsetHeight - window.innerHeight; // アニメーションする全体の高さ

    // 範囲がなければリセットして終了
    if (range <= 0) {
      resetCardStyles();
      return;
    }


    // --- パラメータ取得（モバイル/PCで値を切り替え） ---
    const isMobile = window.innerWidth < MVV_BP_DESKTOP;
    const params = isMobile ? MVV_CARD_PARAMS.mobile : MVV_CARD_PARAMS.desktop;
    const minScale = params.minScale;
    const liftY = params.liftY;
    const nextStartY = params.nextStartY;
    const fadeOutFactor = params.fadeOutFactor;

    // --- 進捗計算 ---
    const normalizedProgress = clampedProgress; // 0〜1
    const activeIndex = Math.min(cards.length - 1, Math.floor(normalizedProgress * cards.length)); // 今のカード
    const sectionProgress = normalizedProgress * cards.length - activeIndex; // 今のカード内での進捗

    // --- 各カードごとにスタイルを設定 ---
    cards.forEach(function(card, index) {
      // すでに通り過ぎたカード
      if (index < activeIndex) {
        card.style.transform = 'scale(' + minScale + ') translateY(-' + liftY + 'px)';
        card.style.opacity = '0';
        card.style.zIndex = String(cards.length - index);
        card.style.pointerEvents = 'none';
        return;
      }

      // 現在表示中のカード
      if (index === activeIndex) {
        // 最後のカードはそのまま表示
        if (index === cards.length - 1) {
          card.style.transform = 'scale(1) translateY(0)';
          card.style.opacity = '1';
          card.style.zIndex = String(cards.length - index);
          card.style.pointerEvents = 'auto';
          return;
        }

        // 途中のカードは進捗に応じて変化
        const scale = 1 - sectionProgress * (1 - minScale);
        const translateY = -sectionProgress * liftY;
        const opacity = Math.max(0, 1 - sectionProgress * fadeOutFactor);

        card.style.transform = 'scale(' + scale + ') translateY(' + translateY + 'px)';
        card.style.opacity = String(opacity);
        card.style.zIndex = String(cards.length - index);
        card.style.pointerEvents = sectionProgress < MVV_ACTIVE_POINTER_THRESHOLD ? 'auto' : 'none';
        return;
      }

      // 次に出てくるカード
      if (index === activeIndex + 1) {
        const nextCardProgress = Math.max(0, Math.min(1, sectionProgress * MVV_NEXT_CARD_PROGRESS_FACTOR));

        card.style.transform = 'scale(1) translateY(' + ((1 - nextCardProgress) * nextStartY) + 'px)';
        card.style.opacity = String(nextCardProgress);
        card.style.zIndex = String(cards.length - index + 10);
        card.style.pointerEvents = nextCardProgress > MVV_NEXT_POINTER_THRESHOLD ? 'auto' : 'none';
        return;
      }

      // それ以外のカードは非表示
      card.style.transform = 'scale(1) translateY(' + nextStartY + 'px)';
      card.style.opacity = '0';
      card.style.zIndex = '1';
      card.style.pointerEvents = 'none';
    });
  };

  // --- 4. 初期化・イベント登録 ---
  const createTrigger = function() {
    if (trigger) {
      trigger.kill();
      trigger = null;
    }

    const range = outer.offsetHeight - window.innerHeight;
    if (range <= 0) {
      resetCardStyles();
      return;
    }

    trigger = ScrollTrigger.create({
      trigger: outer,
      start: MVV_SCROLL_TRIGGER_START,
      end: MVV_SCROLL_TRIGGER_END,
      onUpdate: function(self) {
        update(self.progress);
      },
    });

    update(trigger.progress);
  };

  createTrigger();
  window.addEventListener("resize", createTrigger);
}